import { useEffect, useMemo, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import { Extension } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { Underline } from "@tiptap/extension-underline";
import { cn } from "@shared/utils/cn";

type Alignment = "left" | "center" | "right" | "justify";

const FontSize = Extension.create({
  name: "fontSize",

  addGlobalAttributes() {
    return [
      {
        types: ["textStyle"],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attributes) =>
              attributes.fontSize
                ? { style: `font-size: ${attributes.fontSize}` }
                : {},
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontSize:
        (fontSize: string) =>
        ({ chain }) =>
          chain().setMark("textStyle", { fontSize }).run(),
      unsetFontSize:
        () =>
        ({ chain }) =>
          chain()
            .setMark("textStyle", { fontSize: null })
            .removeEmptyTextStyle()
            .run(),
    };
  },
});

const FONT_SIZE_OPTIONS = [
  { label: "12 px", value: "12px" },
  { label: "14 px", value: "14px" },
  { label: "16 px", value: "16px" },
  { label: "18 px", value: "18px" },
  { label: "24 px", value: "24px" },
  { label: "32 px", value: "32px" },
];

interface RichTextEditorProps {
  value?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  className,
  minHeight = "200px",
}: RichTextEditorProps) {
  const [color, setColor] = useState("#000000");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: { keepMarks: true, keepAttributes: true },
        orderedList: { keepMarks: true, keepAttributes: true },
      }),
      TextStyle,
      Color.configure({ types: ["textStyle"] }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Underline,
      FontSize,
    ],
    content: value || "<p></p>",
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none focus:outline-none min-h-[var(--editor-min-height)]",
        style: `--editor-min-height: ${minHeight};`,
        "data-placeholder": placeholder ?? "",
      },
    },
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor || value === undefined) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [editor, value]);

  const isActive = (action: () => boolean) => (editor ? action() : false);

  const handleAlign = (alignment: Alignment) => {
    if (!editor) return;
    editor.chain().focus().setTextAlign(alignment).run();
  };

  const handleFontSizeChange = (size: string) => {
    if (!editor) return;
    if (size) {
      editor.chain().focus().setFontSize(size).run();
    }
  };

  const resetFontSize = () => {
    editor?.chain().focus().unsetFontSize().run();
  };

  const toolbarButtons = useMemo(
    () => [
      {
        label: "Negrita",
        icon: "B",
        onClick: () => editor?.chain().focus().toggleBold().run(),
        isActive: () => isActive(() => editor!.isActive("bold")),
      },
      {
        label: "Cursiva",
        icon: "I",
        onClick: () => editor?.chain().focus().toggleItalic().run(),
        isActive: () => isActive(() => editor!.isActive("italic")),
      },
      {
        label: "Subrayado",
        icon: "U",
        onClick: () => editor?.chain().focus().toggleUnderline().run(),
        isActive: () => isActive(() => editor!.isActive("underline")),
      },
      {
        label: "Lista ordenada",
        icon: "1.",
        onClick: () => editor?.chain().focus().toggleOrderedList().run(),
        isActive: () => isActive(() => editor!.isActive("orderedList")),
      },
      {
        label: "Lista",
        icon: "•",
        onClick: () => editor?.chain().focus().toggleBulletList().run(),
        isActive: () => isActive(() => editor!.isActive("bulletList")),
      },
      {
        label: "Deshacer",
        icon: "↶",
        onClick: () => editor?.chain().focus().undo().run(),
      },
      {
        label: "Rehacer",
        icon: "↷",
        onClick: () => editor?.chain().focus().redo().run(),
      },
      {
        label: "Limpiar estilo",
        icon: "⨉",
        onClick: () => editor?.chain().focus().unsetAllMarks().run(),
      },
    ],
    [editor],
  );

  return (
    <div className={cn("border rounded-lg bg-white shadow-sm", className)}>
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
        {toolbarButtons.map((button) => (
          <button
            key={button.label}
            type="button"
            onClick={button.onClick}
            className={cn(
              "rounded px-2 py-1 transition-colors",
              button.isActive?.()
                ? "bg-[#282828] text-white"
                : "hover:bg-gray-200",
            )}
            title={button.label}
          >
            {button.icon}
          </button>
        ))}

        <div className="mx-2 h-6 w-px bg-gray-200" />

        <div className="flex items-center gap-1">
          <label htmlFor="rich-text-color" className="text-xs text-gray-500">
            Color
          </label>
          <input
            id="rich-text-color"
            type="color"
            value={color}
            onChange={(event) => {
              const newColor = event.target.value;
              setColor(newColor);
              editor?.chain().focus().setColor(newColor).run();
            }}
            className="h-7 w-7 cursor-pointer border border-gray-300 rounded"
          />
          <button
            type="button"
            onClick={() => editor?.chain().focus().unsetColor().run()}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Reset
          </button>
        </div>

        <div className="mx-2 h-6 w-px bg-gray-200" />

        <div className="flex items-center gap-1">
          <label htmlFor="rich-text-font-size" className="text-xs text-gray-500">
            Tamaño
          </label>
          <select
            id="rich-text-font-size"
            className="rounded border border-gray-300 px-2 py-1 text-xs"
            onChange={(event) => handleFontSizeChange(event.target.value)}
            defaultValue=""
          >
            <option value="" disabled>
              Seleccionar
            </option>
            {FONT_SIZE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={resetFontSize}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Reset
          </button>
        </div>

        <div className="mx-2 h-6 w-px bg-gray-200" />

        <div className="flex items-center gap-1">
          {(["left", "center", "right", "justify"] as Alignment[]).map(
            (alignment) => (
              <button
                key={alignment}
                type="button"
                onClick={() => handleAlign(alignment)}
                className={cn(
                  "rounded px-2 py-1 text-xs capitalize transition-colors",
                  editor?.isActive({ textAlign: alignment })
                    ? "bg-[#282828] text-white"
                    : "hover:bg-gray-200",
                )}
              >
                {alignment}
              </button>
            ),
          )}
        </div>
      </div>

      <EditorContent editor={editor} className="px-3 py-2" />
    </div>
  );
}

